import { AxiosRequestConfig } from 'axios';
import { HttpResponse, ajax } from '../../../../common/ajax';
import { config } from '../../config';
import {
  MostOrderedProductsItemModel,
  MostViewedProductsItemModel,
  OrdersItemModel,
  RevenueItemModel,
} from '../../models/analytics';
import { DropdownOptionModel } from '../../models/common';
import { PeriodModel } from '../../Pages/Analytics/Common/types';

interface IAnalyticsService {
  getOrdersNumber: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<number>>;
  getTotalRevenue: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<number>>;
  getActiveProductsNumber: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<number>>;
  getMostViewedProducts: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<MostViewedProductsItemModel[]>>;
  getMostOrderedProducts: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<MostOrderedProductsItemModel[]>>;
  getRevenue: (
    start: Date,
    end: Date,
    productId?: number,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<RevenueItemModel[]>>;
  getOrders: (
    start: Date,
    end: Date,
    productId?: number,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<OrdersItemModel[]>>;
  downloadExcelReport: (
    filename: string,
    revenuePeriod: PeriodModel,
    revenueProductId: number | null,
    ordersPeriod: PeriodModel,
    ordersProductId: number | null,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<null>>;
  downloadPdfReport: (
    dataUrl: string,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<any>>;
  getHasPayableProducts: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  getProducts: (
    productTypeIds: number[],
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<DropdownOptionModel[]>>;
}

export const analyticsService: IAnalyticsService = {
  getOrdersNumber(axiosConfig) {
    return ajax.get<number>(
      `${config.merchantsApiUrl}/analytics/orders-number`,
      axiosConfig,
    );
  },
  getTotalRevenue(axiosConfig) {
    return ajax.get<number>(
      `${config.merchantsApiUrl}/analytics/total-revenue`,
      axiosConfig,
    );
  },
  getActiveProductsNumber(axiosConfig) {
    return ajax.get<number>(
      `${config.merchantsApiUrl}/analytics/active-products-number`,
      axiosConfig,
    );
  },
  getMostViewedProducts(axiosConfig) {
    return ajax.get<MostViewedProductsItemModel[]>(
      `${config.merchantsApiUrl}/analytics/most-viewed`,
      axiosConfig,
    );
  },
  getMostOrderedProducts(axiosConfig) {
    return ajax.get<MostOrderedProductsItemModel[]>(
      `${config.merchantsApiUrl}/analytics/most-ordered`,
      axiosConfig,
    );
  },
  getRevenue(start, end, productId, axiosConfig) {
    const productIdQuery = productId ? `&productId=${productId}` : '';
    return ajax.get<RevenueItemModel[]>(
      `${
        config.merchantsApiUrl
      }/analytics/revenue?start=${start.toDateString()}&end=${end.toDateString()}${productIdQuery}`,
      axiosConfig,
    );
  },
  getOrders(start, end, productId, axiosConfig) {
    const productIdQuery = productId ? `&productId=${productId}` : '';
    return ajax.get<OrdersItemModel[]>(
      `${
        config.merchantsApiUrl
      }/analytics/orders?start=${start.toDateString()}&end=${end.toDateString()}${productIdQuery}`,
      axiosConfig,
    );
  },
  downloadExcelReport(
    filename,
    revenuePeriod,
    revenueProductId,
    ordersPeriod,
    ordersProductId,
    axiosConfig,
  ) {
    return ajax.downloadFile(
      `${
        config.merchantsApiUrl
      }/analytics/excel-report?revenueStart=${revenuePeriod.startDate.toDateString()}&revenueEnd=${revenuePeriod.endDate.toDateString()}&revenueProductId=${revenueProductId}&ordersStart=${ordersPeriod.startDate.toDateString()}&ordersEnd=${ordersPeriod.endDate.toDateString()}&ordersProductId=${ordersProductId}`,
      filename,
      'xlsx',
      axiosConfig,
    );
  },
  downloadPdfReport(dataUrl, axiosConfig) {
    return ajax.post<any>(
      `${config.merchantsApiUrl}/analytics/pdf-report`,
      { base64Data: dataUrl },
      axiosConfig,
    );
  },
  getHasPayableProducts(axiosConfig) {
    return ajax.get<boolean>(
      `${config.merchantsApiUrl}/analytics/has-payable-products`,
      axiosConfig,
    );
  },
  getProducts(productTypeIds, axiosConfig) {
    const queryString = productTypeIds
      .map(e => `&productTypeIds=${e}`)
      .join('');
    return ajax.get<DropdownOptionModel[]>(
      `${config.merchantsApiUrl}/analytics/products?${queryString}`,
      axiosConfig,
    );
  },
};
